import type { Locale } from './locales.ts';

/**
 * Money, numbers and dates.
 *
 * ## The rule this file exists to enforce
 *
 * **Nothing here does arithmetic on money.** Every subtotal, VAT figure and total is computed
 * server-side in `decimal` and arrives already correct. These functions turn a number into a
 * string and do nothing else.
 *
 * Floating-point money in JavaScript fails silently — `0.1 + 0.2` is `0.30000000000000004` — so
 * a cart that adds up its own lines will disagree with the order it produces by a fils on some
 * baskets and not others. If a total is needed, ask the API for it.
 */

const CURRENCY_LABEL: Record<Locale, string> = {
  en: 'AED',
  ar: 'د.إ',
};

/**
 * Two decimals, always, with Western digits and a dot — **in both languages**.
 *
 * `Intl.NumberFormat('ar')` renders Arabic-Indic digits (٤٥٫٠٠) by default. UAE retail, printed
 * menus and bank statements all use Western digits, and a price that changes shape when the
 * language switches makes two pages hard to compare. `en-US` is hardcoded rather than passed
 * the caller's locale precisely so this cannot drift.
 */
export function formatAmount(amount: number, { grouped = false }: { grouped?: boolean } = {}): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: grouped,
  }).format(amount);
}

/**
 * An amount with its currency, ordered for the locale: `AED 45.00` in English,
 * `45.00 د.إ` in Arabic.
 *
 * Wrap the result in an element with `dir="ltr"` (or a `<bdi>`) when it sits inside Arabic prose
 * — otherwise the bidi algorithm reorders the neutral run and displays `د.إ 45.00`.
 *
 * `grouped` adds thousands separators. Prices never use it; a back-office total running to five
 * figures does, where "AED 16789.00" takes a second look to read.
 */
export function formatMoney(
  locale: Locale,
  amount: number,
  options: { grouped?: boolean } = {},
): string {
  const value = formatAmount(amount, options);

  return locale === 'ar' ? `${value} ${CURRENCY_LABEL.ar}` : `${CURRENCY_LABEL.en} ${value}`;
}

export function currencyLabel(locale: Locale): string {
  return CURRENCY_LABEL[locale];
}

/** A plain integer — item counts, review counts. Grouped for readability above a thousand. */
export function formatNumber(locale: Locale, value: number): string {
  return new Intl.NumberFormat(locale === 'ar' ? 'ar-AE-u-nu-latn' : 'en-AE').format(value);
}

/** A rating to one decimal, matching what the API computes. */
export function formatRating(value: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * A date in the reader's language but always on the Gregorian calendar with Western digits.
 *
 * `Intl` would otherwise give Arabic readers the Islamic calendar in some environments, and an
 * order placed on `١٥ رمضان` cannot be matched against a delivery slip that says `2026-03-05`.
 */
export function formatDate(locale: Locale, value: Date | string): string {
  const date = typeof value === 'string' ? new Date(value) : value;

  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-AE-u-ca-gregory-nu-latn' : 'en-AE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

export function formatDateTime(locale: Locale, value: Date | string): string {
  const date = typeof value === 'string' ? new Date(value) : value;

  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-AE-u-ca-gregory-nu-latn' : 'en-AE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/** "3 days ago" — for order timelines and review dates. */
export function formatRelative(locale: Locale, value: Date | string, now: Date = new Date()): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  const seconds = Math.round((date.getTime() - now.getTime()) / 1000);

  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 60 * 60 * 24 * 365],
    ['month', 60 * 60 * 24 * 30],
    ['week', 60 * 60 * 24 * 7],
    ['day', 60 * 60 * 24],
    ['hour', 60 * 60],
    ['minute', 60],
  ];

  const formatter = new Intl.RelativeTimeFormat(locale === 'ar' ? 'ar-AE-u-nu-latn' : 'en-AE', {
    numeric: 'auto',
  });

  for (const [unit, secondsInUnit] of units) {
    if (Math.abs(seconds) >= secondsInUnit) {
      return formatter.format(Math.round(seconds / secondsInUnit), unit);
    }
  }

  return formatter.format(0, 'second');
}

/**
 * A percentage for a discount badge. Whole numbers only — "23.4% off" is noise.
 */
export function formatPercent(locale: Locale, whole: number): string {
  return locale === 'ar' ? `${Math.round(whole)}%` : `${Math.round(whole)}%`;
}
